import React from "react";
import { Scale, ExternalLink, Shield } from "lucide-react";

/**
 * Sponsored ad slot for PrivateCourt.ai.
 * Clearly labeled "Sponsored" to keep editorial trust.
 *
 * Variants:
 *   - "banner": wide homepage / in-feed banner
 *   - "sidebar": sidebar card on article page
 *   - "inline": compact inline ad between article sections
 */
export default function AdBlock({ variant = "banner" }) {
  const content = (
    <>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--io-muted)]">Sponsored</span>
        <Shield size={12} className="text-[var(--io-gold)]" />
      </div>
      <div className={variant === "banner" ? "grid md:grid-cols-12 gap-6 items-center" : ""}>
        <div className={variant === "banner" ? "md:col-span-8" : ""}>
          <div className="inline-flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-md border border-[var(--io-gold)]/30 bg-gradient-to-br from-[var(--io-gold)]/10 to-transparent">
              <Scale size={14} className="text-[var(--io-gold)]" />
            </span>
            <span className="font-serif text-[16px] text-[var(--io-text)]">PrivateCourt<span className="text-[var(--io-gold)]">.ai</span></span>
          </div>
          <h4 className={`font-serif ${variant === "banner" ? "text-[24px] md:text-[28px]" : "text-[17px]"} leading-snug text-[var(--io-text)]`}>
            India's AI-powered arbitration & ODR platform.
          </h4>
          <p className={`mt-2 text-[13px] text-[var(--io-text-2)] ${variant === "sidebar" ? "line-clamp-3" : ""}`}>
            Initiate disputes online, get matched with neutrals, hold secure digital hearings, and receive enforceable awards — for businesses, MSMEs, and counsel.
          </p>
        </div>
        <div className={variant === "banner" ? "md:col-span-4 md:justify-self-end" : "mt-4"}>
          <a
            href="https://privatecourt.ai"
            target="_blank"
            rel="noopener sponsored"
            className="btn btn-primary !text-[12px] !py-2.5 !px-4 inline-flex"
            data-testid="adblock-cta"
          >
            Visit PrivateCourt.ai <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </>
  );

  const base = "io-card-el p-5 md:p-6 relative overflow-hidden";
  const intelGrid = <div className="absolute inset-0 intel-grid opacity-25 pointer-events-none" />;

  return (
    <aside className={base} role="complementary" aria-label="Sponsored — PrivateCourt.ai" data-testid={`adblock-${variant}`}>
      {intelGrid}
      <div className="relative">{content}</div>
    </aside>
  );
}
